import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Admin credentials (in production, this should be in a secure database)
const ADMIN_CREDENTIALS = {
  email: 'admin@mymapleshop.com',
  password: 'admin123' // Change this to a secure password
};

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [users, setUsers] = useState([]);

  // Load users and current user from storage on mount
  useEffect(() => {
    const savedUsers = localStorage.getItem('users');
    const savedCurrentUser = localStorage.getItem('currentUser');
    
    if (savedUsers) {
      setUsers(JSON.parse(savedUsers));
    }
    
    if (savedCurrentUser) {
      const user = JSON.parse(savedCurrentUser);
      setCurrentUser(user);
      setIsAdmin(user.isAdmin || false);
    }
  }, []);

  const signup = (email, password, name) => {
    // Check if user already exists
    const existingUser = users.find(u => u.email === email);
    if (existingUser) {
      throw new Error('User already exists');
    }

    const newUser = {
      id: Date.now(),
      email,
      password, // In production, hash this!
      name,
      isAdmin: false,
      createdAt: new Date().toISOString()
    };

    const updatedUsers = [...users, newUser];
    setUsers(updatedUsers);
    localStorage.setItem('users', JSON.stringify(updatedUsers));

    return newUser;
  };

  const signin = (email, password) => {
    // Check for admin login
    if (email === ADMIN_CREDENTIALS.email && password === ADMIN_CREDENTIALS.password) {
      const adminUser = {
        id: 'admin',
        email: ADMIN_CREDENTIALS.email,
        name: 'Admin',
        isAdmin: true
      };
      setCurrentUser(adminUser);
      setIsAdmin(true);
      localStorage.setItem('currentUser', JSON.stringify(adminUser));
      return adminUser;
    }

    // Check for regular user login
    const user = users.find(u => u.email === email && u.password === password);
    if (!user) {
      throw new Error('Invalid email or password');
    }

    setCurrentUser(user);
    setIsAdmin(false);
    localStorage.setItem('currentUser', JSON.stringify(user));
    return user;
  };

  const signout = () => {
    setCurrentUser(null);
    setIsAdmin(false);
    localStorage.removeItem('currentUser');
  };

  const value = {
    currentUser,
    isAdmin,
    signup,
    signin,
    signout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}